import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, 
  Plus, 
  Mail,
  Phone,
  Building2,
  User,
  MessageSquare,
  CheckSquare,
  ArrowUpDown,
  Filter,
  Send,
  Ban,
  ShieldAlert,
  ShieldOff,
  Trash2,
  FolderOpen,
  Loader2,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import EmpresaDetailModal, { formatPhone, handlePhoneInput } from './EmpresaDetailModal';
import * as api from '../services/api';

const EMPRESAS_PER_PAGE = 30; // Load 30 at a time for better performance

const EmpresasPage = ({ currentUser, canDelete = () => true, globalSearch = '' }) => {
  const [empresas, setEmpresas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('mine'); // Default to current user's empresas
  const [blacklistFilter, setBlacklistFilter] = useState('active'); // 'active', 'blacklisted', 'all'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [blacklistTarget, setBlacklistTarget] = useState(null);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Ref for infinite scroll observer
  const loadMoreRef = useRef(null);

  // Combine local search with global search
  const effectiveSearchTerm = globalSearch || searchTerm;

  const isAdmin = currentUser?.role === 'administrator' || currentUser?.role === 'admin';

  const [newEmpresa, setNewEmpresa] = useState({
    name: '',
    category: 'Empresa',
    email: '',
    phone: '',
    website: '',
    address: '',
    industry: '',
    assigned_to: '',
    stage: 'Lead'
  });

  // Load initial data with pagination
  const loadData = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Determine sort field
      let sortBy = 'created_at';
      let sortDir = 'desc';
      
      if (sortOrder === 'newest') {
        sortBy = 'created_at';
        sortDir = 'desc';
      } else if (sortOrder === 'oldest') {
        sortBy = 'created_at';
        sortDir = 'asc';
      } else if (sortOrder === 'alpha_asc') {
        sortBy = 'name';
        sortDir = 'asc';
      } else if (sortOrder === 'alpha_desc') {
        sortBy = 'name';
        sortDir = 'desc';
      }
      
      // Determine assigned_to filter
      const assignedTo = userFilter === 'mine' ? currentUser?.name : 
                         userFilter === 'all' ? null : userFilter;
      
      const category = categoryFilter !== 'all' ? categoryFilter : null;
      
      // Fetch empresas with pagination and count in parallel
      const requests = [
        api.getEmpresasSorted(sortBy, sortDir, category, assignedTo, page, EMPRESAS_PER_PAGE)
      ];
      
      // Only fetch users and count on first load
      if (page === 1) {
        requests.push(api.getUsers());
        requests.push(api.getEmpresasCount(assignedTo, category));
      }
      
      const results = await Promise.all(requests);
      const empresasRes = results[0];
      
      if (page === 1) {
        setEmpresas(empresasRes.data);
        setUsers(results[1]?.data || []);
        setTotalCount(results[2]?.data?.count || 0);
      } else if (append) {
        setEmpresas(prev => [...prev, ...empresasRes.data]);
      }
      
      // Check if there are more to load
      setHasMore(empresasRes.data.length === EMPRESAS_PER_PAGE);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sortOrder, categoryFilter, userFilter, currentUser?.name]);

  // Load more empresas
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadData(currentPage + 1, true);
    }
  }, [loadData, currentPage, loadingMore, hasMore]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setEmpresas([]);
    loadData(1, false);
  }, [sortOrder, categoryFilter, userFilter, currentUser?.name]);

  const filteredEmpresas = empresas.filter(empresa => {
    const matchesSearch = empresa.name?.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
                          empresa.email?.toLowerCase().includes(effectiveSearchTerm.toLowerCase());
    
    // Blacklist filter
    if (blacklistFilter === 'active' && empresa.is_blacklisted) return false;
    if (blacklistFilter === 'blacklisted' && !empresa.is_blacklisted) return false;
    
    return matchesSearch;
  });

  const handleCreateEmpresa = async () => {
    if (!newEmpresa.name) {
      alert('El nombre es requerido');
      return;
    }
    try {
      const empresaData = {
        ...newEmpresa,
        assigned_to: newEmpresa.assigned_to || currentUser?.name || 'Sin asignar'
      };
      await api.createEmpresa(empresaData);
      setShowAddModal(false);
      setNewEmpresa({
        name: '', category: 'Empresa', email: '', phone: '', website: '',
        address: '', industry: '', assigned_to: '', stage: 'Lead'
      });
      loadData();
    } catch (error) {
      console.error('Error creating empresa:', error);
      alert('Error al crear empresa');
    }
  };

  const openBlacklistModal = (empresa, e) => {
    e.stopPropagation();
    setBlacklistTarget(empresa);
    setBlacklistReason('');
    setShowBlacklistModal(true);
  };

  const handleToggleBlacklist = async () => {
    if (!blacklistTarget) return;
    
    try {
      // If currently NOT blacklisted, require a reason
      if (!blacklistTarget.is_blacklisted && !blacklistReason.trim()) {
        alert('Por favor, proporcione un motivo para añadir a la lista negra');
        return;
      }
      
      const response = await api.toggleBlacklist(blacklistTarget.id, blacklistReason);
      
      // Show result message
      if (!blacklistTarget.is_blacklisted) {
        const deletedDeals = response.data?.deleted_deals || 0;
        const deletedTasks = response.data?.deleted_tasks || 0;
        alert(`Empresa añadida a la lista negra.\n\nSe eliminaron:\n- ${deletedDeals} deal(s) de los embudos\n- ${deletedTasks} tarea(s) pendiente(s)`);
      } else {
        alert('Empresa removida de la lista negra.');
      }
      
      setShowBlacklistModal(false);
      setBlacklistTarget(null);
      setBlacklistReason('');
      loadData();
    } catch (error) {
      console.error('Error toggling blacklist:', error);
      alert('Error al modificar la lista negra');
    }
  };

  const openDeleteConfirm = (empresa, e) => {
    e.stopPropagation();
    setDeleteTarget(empresa);
    setShowDeleteConfirm(true);
  };

  const handleDeleteEmpresa = async () => {
    if (!deleteTarget) return;
    
    try {
      await api.deleteEmpresa(deleteTarget.id);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting empresa:', error);
      alert('Error al eliminar la empresa');
    }
  };

  const openDetailModal = (empresa) => {
    setSelectedEmpresaId(empresa.id);
    setShowDetailModal(true);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Lead': 'bg-blue-100 text-blue-700',
      'Contactado': 'bg-cyan-100 text-cyan-700',
      'Propuesta': 'bg-yellow-100 text-yellow-700',
      'Negociación': 'bg-orange-100 text-orange-700',
      'Cerrado Ganado': 'bg-green-100 text-green-700',
      'Cerrado Perdido': 'bg-red-100 text-red-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'Empresa': 'bg-blue-100 text-blue-800',
      'Artista': 'bg-purple-100 text-purple-800',
      'Talento': 'bg-orange-100 text-orange-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Empresas</h1>
          <p className="text-gray-500 text-sm">
            {effectiveSearchTerm 
              ? `${filteredEmpresas.length} resultados` 
              : `Mostrando ${empresas.length} de ${totalCount} empresas`}
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="add-empresa-btn">
          <Plus size={18} className="mr-2" />
          Nueva Empresa
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar empresas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-empresas"
          />
        </div>
        <Select value={userFilter} onValueChange={setUserFilter} data-testid="user-filter">
          <SelectTrigger className="w-44">
            <User size={16} className="mr-2" />
            <SelectValue placeholder="Usuario" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mine">Mis empresas</SelectItem>
            {isAdmin && <SelectItem value="all">Todas</SelectItem>}
            {isAdmin && users.map(user => (
              <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={blacklistFilter} onValueChange={setBlacklistFilter} data-testid="blacklist-filter">
          <SelectTrigger className="w-44">
            <Ban size={16} className="mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Activas</SelectItem>
            <SelectItem value="blacklisted">Lista Negra</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-36">
            <Filter size={16} className="mr-2" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="Empresa">Empresa</SelectItem>
            <SelectItem value="Artista">Artista</SelectItem>
            <SelectItem value="Talento">Talento</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-40">
            <ArrowUpDown size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Más recientes</SelectItem>
            <SelectItem value="oldest">Más antiguas</SelectItem>
            <SelectItem value="alpha_asc">A-Z</SelectItem>
            <SelectItem value="alpha_desc">Z-A</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Empresas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmpresas.map((empresa) => (
          <Card 
            key={empresa.id} 
            className={`cursor-pointer hover:shadow-lg transition-all ${
              empresa.is_blacklisted 
                ? 'border-red-300 bg-red-50/50 hover:border-red-400' 
                : 'hover:border-[#D4A843]'
            }`}
            onClick={() => openDetailModal(empresa)}
            data-testid={`empresa-card-${empresa.id}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    empresa.is_blacklisted ? 'bg-red-100' : 'bg-[#D4A843]/10'
                  }`}>
                    {empresa.is_blacklisted ? (
                      <ShieldAlert size={20} className="text-red-600" />
                    ) : (
                      <Building2 size={20} className="text-[#D4A843]" />
                    )}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${empresa.is_blacklisted ? 'text-red-800' : 'text-gray-800'}`}>
                      {empresa.name}
                    </h3>
                    <div className="flex items-center gap-1">
                      <Badge className={getCategoryBadge(empresa.category)}>{empresa.category}</Badge>
                      {empresa.is_blacklisted && (
                        <Badge className="bg-red-100 text-red-700 text-xs">Lista Negra</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={getStageColor(empresa.stage)}>{empresa.stage}</Badge>
                  <button
                    onClick={(e) => openBlacklistModal(empresa, e)}
                    className={`p-1.5 rounded-full transition-colors ${
                      empresa.is_blacklisted 
                        ? 'hover:bg-green-100 text-green-600' 
                        : 'hover:bg-red-100 text-red-500'
                    }`}
                    title={empresa.is_blacklisted ? 'Quitar de Lista Negra' : 'Añadir a Lista Negra'}
                    data-testid={`blacklist-btn-${empresa.id}`}
                  >
                    {empresa.is_blacklisted ? (
                      <ShieldOff size={16} />
                    ) : (
                      <Ban size={16} />
                    )}
                  </button>
                  {canDelete(empresa) && (
                    <button
                      onClick={(e) => openDeleteConfirm(empresa, e)}
                      className="p-1.5 rounded-full hover:bg-red-100 text-red-500 transition-colors"
                      title="Eliminar empresa"
                      data-testid={`delete-empresa-btn-${empresa.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              {empresa.is_blacklisted && empresa.blacklist_reason && (
                <div className="mb-3 p-2 bg-red-100 rounded-lg">
                  <p className="text-xs text-red-700">
                    <strong>Motivo:</strong> {empresa.blacklist_reason}
                  </p>
                </div>
              )}
              
              <div className="space-y-2 text-sm text-gray-600">
                {empresa.email && (
                  <div className="flex items-center">
                    <Mail size={14} className="mr-2 text-gray-400" />
                    <span className="truncate">{empresa.email}</span>
                  </div>
                )}
                {empresa.phone && (
                  <div className="flex items-center">
                    <Phone size={14} className="mr-2 text-gray-400" />
                    <span>{formatPhone(empresa.phone)}</span>
                  </div>
                )}
                {empresa.assigned_to && (
                  <div className="flex items-center">
                    <User size={14} className="mr-2 text-gray-400" />
                    <span>{empresa.assigned_to}</span>
                  </div>
                )}
              </div>

              {/* Contacts Preview */}
              {empresa.contacts?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1 font-medium">Contactos:</p>
                  <div className="space-y-1">
                    {empresa.contacts.slice(0, 2).map((contact, idx) => (
                      <div key={idx} className="text-xs text-gray-600 truncate">
                        <span className="font-medium">{contact.name}</span>
                        {contact.position && <span className="text-gray-400"> - {contact.position}</span>}
                      </div>
                    ))}
                    {empresa.contacts.length > 2 && (
                      <p className="text-xs text-gray-400">+{empresa.contacts.length - 2} más</p>
                    )}
                  </div>
                </div>
              )}

              {/* Notes Preview */}
              {empresa.notes?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1 font-medium">Última nota:</p>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {empresa.notes[empresa.notes.length - 1]?.content}
                  </p>
                </div>
              )}

              {/* Quick stats and Email button */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center">
                    <User size={12} className="mr-1" />
                    {empresa.contacts?.length || 0}
                  </span>
                  <span className="flex items-center">
                    <MessageSquare size={12} className="mr-1" />
                    {empresa.notes?.length || 0}
                  </span>
                  <span className="flex items-center">
                    <CheckSquare size={12} className="mr-1" />
                    {empresa.tasks?.length || 0}
                  </span>
                  {empresa.documents?.length > 0 && (
                    <span className="flex items-center text-[#D4A843]" title="Documentos adjuntos">
                      <FolderOpen size={12} className="mr-1" />
                      {empresa.documents.length}
                    </span>
                  )}
                </div>
                {(empresa.email || empresa.contacts?.some(c => c.email)) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedEmpresaId(empresa.id); setShowDetailModal(true); }}
                    className="p-1.5 hover:bg-[#D4A843]/10 rounded-full transition-colors"
                    title="Ver detalles / Enviar email"
                  >
                    <Send size={14} className="text-[#D4A843]" />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && filteredEmpresas.length > 0 && !effectiveSearchTerm && (
        <div className="flex justify-center mt-6" ref={loadMoreRef}>
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-[200px]"
            data-testid="load-more-empresas"
          >
            {loadingMore ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cargando...
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 h-4 w-4" />
                Cargar más ({empresas.length} de {totalCount})
              </>
            )}
          </Button>
        </div>
      )}

      {filteredEmpresas.length === 0 && !loading && (
        <div className="text-center py-8">
          <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No se encontraron empresas</p>
        </div>
      )}

      {/* Add Empresa Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <Input
                  value={newEmpresa.name}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, name: e.target.value })}
                  placeholder="Nombre de la empresa"
                  data-testid="new-empresa-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <Select 
                  value={newEmpresa.category} 
                  onValueChange={(value) => setNewEmpresa({ ...newEmpresa, category: value })}
                >
                  <SelectTrigger data-testid="new-empresa-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Empresa">Empresa</SelectItem>
                    <SelectItem value="Artista">Artista</SelectItem>
                    <SelectItem value="Talento">Talento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={newEmpresa.email}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                  data-testid="new-empresa-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <Input
                  value={newEmpresa.phone}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, phone: handlePhoneInput(e.target.value) })}
                  placeholder="(787) 555-1234"
                  data-testid="new-empresa-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <Input
                  value={newEmpresa.website}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, website: e.target.value })}
                  placeholder="https://ejemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <Input
                  value={newEmpresa.address}
                  onChange={(e) => setNewEmpresa({ ...newEmpresa, address: e.target.value })}
                  placeholder="Dirección"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a</label>
                <Select 
                  value={newEmpresa.assigned_to} 
                  onValueChange={(value) => setNewEmpresa({ ...newEmpresa, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etapa</label>
                <Select 
                  value={newEmpresa.stage} 
                  onValueChange={(value) => setNewEmpresa({ ...newEmpresa, stage: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lead">Lead</SelectItem>
                    <SelectItem value="Contactado">Contactado</SelectItem>
                    <SelectItem value="Propuesta">Propuesta</SelectItem>
                    <SelectItem value="Negociación">Negociación</SelectItem>
                    <SelectItem value="Cerrado Ganado">Cerrado Ganado</SelectItem>
                    <SelectItem value="Cerrado Perdido">Cerrado Perdido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateEmpresa} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="save-empresa-btn">
              Crear Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empresa Detail Modal */}
      <EmpresaDetailModal
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        empresaId={selectedEmpresaId}
        currentUser={currentUser}
        onEmpresaUpdate={() => loadData()}
        onEmpresaDelete={() => { setShowDetailModal(false); loadData(); }}
        canDelete={canDelete}
      />

      {/* Blacklist Modal */}
      <Dialog open={showBlacklistModal} onOpenChange={setShowBlacklistModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {blacklistTarget?.is_blacklisted ? (
                <>
                  <ShieldOff size={20} className="text-green-600" />
                  Quitar de Lista Negra
                </>
              ) : (
                <>
                  <Ban size={20} className="text-red-600" />
                  Añadir a Lista Negra
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {blacklistTarget && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">{blacklistTarget.name}</p>
                <p className="text-sm text-gray-500">{blacklistTarget.category}</p>
              </div>
              
              {blacklistTarget.is_blacklisted ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ¿Está seguro de que desea quitar esta empresa de la lista negra? 
                    Se volverá a mostrar en las listas normales.
                  </p>
                  {blacklistTarget.blacklist_reason && (
                    <p className="mt-2 text-sm text-yellow-700">
                      <strong>Motivo original:</strong> {blacklistTarget.blacklist_reason}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                    <p className="text-sm text-red-800 font-medium">
                      ⚠️ Al añadir a la lista negra se eliminarán automáticamente:
                    </p>
                    <ul className="text-sm text-red-700 list-disc list-inside">
                      <li>Todos los deals de esta empresa en los embudos</li>
                      <li>Todas las tareas pendientes relacionadas</li>
                    </ul>
                    <p className="text-xs text-red-600 mt-2">
                      Esta acción no se puede deshacer.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo de la lista negra *
                    </label>
                    <textarea
                      value={blacklistReason}
                      onChange={(e) => setBlacklistReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      rows={3}
                      placeholder="Ej: No pago de facturas, comportamiento problemático, etc."
                      data-testid="blacklist-reason-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBlacklistModal(false); setBlacklistTarget(null); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleToggleBlacklist}
              className={blacklistTarget?.is_blacklisted ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-blacklist-btn"
            >
              {blacklistTarget?.is_blacklisted ? 'Quitar de Lista Negra' : 'Añadir a Lista Negra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Trash2 size={20} className="mr-2" />
              Eliminar Empresa
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Building2 size={24} className="text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{deleteTarget.name}</p>
                  <p className="text-sm text-gray-500">{deleteTarget.category}</p>
                </div>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Esta acción eliminará permanentemente:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside">
                  <li>Toda la información de la empresa</li>
                  <li>{deleteTarget.contacts?.length || 0} contacto(s)</li>
                  <li>{deleteTarget.notes?.length || 0} nota(s)</li>
                  <li>{deleteTarget.tasks?.length || 0} tarea(s)</li>
                  <li>{deleteTarget.documents?.length || 0} documento(s)</li>
                  <li>Todos los deals asociados</li>
                </ul>
                <p className="text-xs text-red-600 mt-2">
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteEmpresa}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-empresa-btn"
            >
              <Trash2 size={16} className="mr-1" />
              Eliminar Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpresasPage;
